/**
 * Idioma vem do PREFIXO da URL, não de detecção automática de navegador.
 *
 * Por que não detectar: o público principal é brasileiro e um redirecionamento
 * automático mandaria parte dele para uma página em inglês que não converte.
 * Prefixo explícito também dá a cada idioma uma URL própria, que é o que o
 * Google indexa e o que o hreflang aponta.
 *
 *   /            -> pt-BR (mercado principal, sem prefixo, não quebra link existente)
 *   /en, /en/... -> inglês
 *   /es, /es/... -> espanhol
 */

export const LOCALES = ['pt-BR', 'en', 'es'] as const;
export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = 'pt-BR';

/** Prefixo de URL de cada idioma. O padrão não tem prefixo. */
const PREFIX: Record<Locale, string> = { 'pt-BR': '', en: '/en', es: '/es' };

export const LOCALE_LABELS: Record<Locale, string> = {
  'pt-BR': 'Português',
  en: 'English',
  es: 'Español',
};

/** Código para o atributo lang do <html> e para as tags hreflang. */
export const HTML_LANG: Record<Locale, string> = { 'pt-BR': 'pt-BR', en: 'en', es: 'es' };

/**
 * Separa o prefixo de idioma do resto do caminho. Devolver o caminho já sem
 * prefixo permite que todo o roteamento existente continue casando as mesmas
 * expressões, sem saber que idioma existe.
 */
export function splitLocale(pathname: string): { locale: Locale; path: string } {
  const match = pathname.match(/^\/(en|es)(?=\/|$)/);
  if (!match) return { locale: DEFAULT_LOCALE, path: pathname };
  const locale = match[1] as Locale;
  const rest = pathname.slice(match[0].length);
  return { locale, path: rest === '' ? '/' : rest };
}

export function getLocaleFromPath(pathname: string): Locale {
  return splitLocale(pathname).locale;
}

/** Monta uma URL interna já no idioma pedido. `path` sempre começa com "/". */
export function localizedPath(path: string, locale: Locale): string {
  const clean = path.startsWith('/') ? path : `/${path}`;
  // A home é caso especial: concatenar o prefixo com "/" geraria "/en/", e o
  // resto do site linka "/en". Duas URLs para a mesma página dividem o sinal de
  // indexação, justamente o que o hreflang existe para evitar.
  const prefixed = clean === '/' ? PREFIX[locale] : `${PREFIX[locale]}${clean}`;
  return prefixed === '' ? '/' : prefixed;
}

/** Mesma página, outro idioma — usado pelo seletor e pelas tags hreflang. */
export function swapLocale(pathname: string, target: Locale): string {
  const { path } = splitLocale(pathname);
  return localizedPath(path, target);
}

const STORAGE_KEY = 'meugantt_locale';

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

export function persistLocale(locale: Locale): void {
  try { localStorage.setItem(STORAGE_KEY, locale); } catch { /* storage indisponível */ }
}

export function readPersistedLocale(): Locale | null {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return isLocale(saved) ? saved : null;
  } catch {
    return null;
  }
}

/**
 * Idioma efetivo: prefixo da URL > preferência salva > padrão.
 *
 * O prefixo vence porque é intenção explícita e compartilhável, e ao vencer ele
 * PERSISTE. É isso que faz quem chega pela landing em inglês continuar em inglês
 * depois de logar, já que o app autenticado não tem prefixo de idioma na URL.
 *
 * Não há detecção por navegador de propósito: mandar o público brasileiro para
 * uma página em inglês destruiria a conversão do mercado principal.
 */
export function resolveLocale(pathname: string = window.location.pathname): Locale {
  const fromPath = splitLocale(pathname);
  if (fromPath.locale !== DEFAULT_LOCALE) {
    persistLocale(fromPath.locale);
    return fromPath.locale;
  }
  return readPersistedLocale() ?? DEFAULT_LOCALE;
}
